import React, { useEffect, useState } from 'react';
import { Activity, IndianRupee, Users, Calendar, Trophy, Building2, Percent, Download } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { KPI, Badge } from '@/components/ui/Card';
import { formatINR } from '@/lib/utils';
import { useBranding } from '@/lib/branding';

// Phase 5: super_admin's own dashboard — platform-wide totals plus an
// academy-wise breakdown. Replaces the per-venue AdminDashboard, which
// Phase 4 restricted to venue admins only (super_admin is scoped to venue
// creation + staff assignment, not day-to-day venue operations).
export default function AdminPlatformStats() {
  const [stats, setStats] = useState(null);
  const { primary_color } = useBranding();

  useEffect(() => {
    api.get('/admin/platform/stats').then((r) => setStats(r.data));
  }, []);

  if (!stats) return <div className="text-white/40 text-sm">Loading platform stats…</div>;

  const exportCSV = () => {
    const headers = ['Venue', 'Status', 'Revenue', 'Bookings', 'Active Members', 'Booking Fee (INR)', 'Platform Fee Revenue'];
    const lines = stats.academy_wise.map((a) => [
      a.academy_name, a.active ? 'Active' : 'Inactive', a.revenue, a.bookings,
      a.active_memberships, a.booking_fee ?? '', a.platform_fee_revenue,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `kba-platform-academies-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="admin-platform-stats">
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Platform</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight">Overview</h1>
        </div>
        <Button variant="outline" onClick={exportCSV} data-testid="export-academy-breakdown">
          <Download size={14} /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Platform Revenue" value={formatINR(stats.total_revenue)} icon={IndianRupee} tone="volt" />
        <KPI label="Platform Fee Revenue" value={formatINR(stats.platform_fee_revenue_total)} icon={Percent} hint="Per-booking & per-plan fees billed to venues" />
        <KPI label="Active Venues" value={`${stats.active_academies} / ${stats.total_academies}`} icon={Building2} />
        <KPI label="Bookings Today" value={stats.bookings_today} icon={Activity} />
        <KPI label="Bookings This Month" value={stats.bookings_this_month} icon={Calendar} />
        <KPI label="Memberships This Month" value={stats.memberships_this_month} icon={Trophy} />
        <KPI label="Customers" value={stats.total_customers} icon={Users} />
        <KPI label="Active Members" value={stats.active_memberships} icon={Trophy} />
      </div>

      <div className="bg-surface-raised border border-white/10 p-6">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Last 14 days</div>
          <h3 className="font-heading font-bold text-xl uppercase">Platform revenue trend</h3>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={stats.revenue_series}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} tickFormatter={(d) => d?.slice(5)} />
              <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} />
              <Tooltip
                contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 0 }}
                labelStyle={{ color: '#fff', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em' }}
                formatter={(v) => formatINR(v)}
              />
              <Line type="monotone" dataKey="revenue" stroke={primary_color} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-surface-raised border border-white/10 p-6">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Academy-wise</div>
          <h3 className="font-heading font-bold text-xl uppercase">Venue breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/10">
                <th className="pb-3 pr-4">Venue</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 pr-4">Revenue</th>
                <th className="pb-3 pr-4">Bookings</th>
                <th className="pb-3 pr-4">Active Members</th>
                <th className="pb-3 pr-4">Booking Fee</th>
                <th className="pb-3">Fee Revenue</th>
              </tr>
            </thead>
            <tbody>
              {stats.academy_wise.map((a) => (
                <tr key={a.academy_id} className="border-b border-white/5">
                  <td className="py-3 pr-4 font-medium">{a.academy_name}</td>
                  <td className="py-3 pr-4"><Badge tone={a.active ? 'success' : 'default'}>{a.active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="py-3 pr-4">{formatINR(a.revenue)}</td>
                  <td className="py-3 pr-4">{a.bookings}</td>
                  <td className="py-3 pr-4">{a.active_memberships}</td>
                  <td className="py-3 pr-4">{a.booking_fee ? formatINR(a.booking_fee) : '—'}</td>
                  <td className="py-3">{formatINR(a.platform_fee_revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
